import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LicenseInfo, LicenseTier } from './license.model';

const EDUCATION_LICENSE_BODY_CLASS = 'icu-edu-license';

interface LicenseApiResponse {
  tier?: string;
  inGracePeriod: boolean;
  graceDaysRemaining: number;
  expiresAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LicenseService {
  private info: LicenseInfo = { tier: null, inGracePeriod: false, graceDaysRemaining: 0 };

  constructor(@Inject(DOCUMENT) private doc: Document, private http: HttpClient) { }

  async load(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<LicenseApiResponse>(`${environment.baseApiUrl}/license`)
      );
      this.info = {
        tier: this.parseTier(res?.tier),
        inGracePeriod: res?.inGracePeriod === true,
        graceDaysRemaining: res?.graceDaysRemaining ?? 0,
        expiresAt: res?.expiresAt ?? undefined
      };
    } catch {
      this.info = { tier: null, inGracePeriod: false, graceDaysRemaining: 0 };
    }
    this.applyBodyClass();
  }

  get tier(): LicenseTier | null {
    return this.info.tier;
  }

  get isEducation(): boolean {
    return this.info.tier === 'education';
  }

  get isGovernment(): boolean {
    return this.info.tier === 'government';
  }

  get isInGracePeriod(): boolean {
    return this.info.inGracePeriod;
  }

  get graceDaysRemaining(): number {
    return this.info.graceDaysRemaining;
  }

  get expiresAt(): string | undefined {
    return this.info.expiresAt;
  }

  get daysUntilExpiry(): number | null {
    if (!this.info.expiresAt) {
      return null;
    }
    const expiryMs = new Date(this.info.expiresAt).getTime();
    if (isNaN(expiryMs)) {
      return null;
    }
    const diffMs = expiryMs - Date.now();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }

  get isExpired(): boolean {
    return !this.info.inGracePeriod && this.daysUntilExpiry === 0;
  }

  private parseTier(raw: string | undefined): LicenseTier | null {
    if (raw === 'government') {
      return 'government';
    }
    if (raw === 'education') {
      return 'education';
    }
    return null;
  }

  private applyBodyClass(): void {
    this.doc.body.classList.toggle(EDUCATION_LICENSE_BODY_CLASS, this.isEducation);
  }
}
